import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Search } from "lucide-react";
import { api, DOC_LABELS, money } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Input } from "@/components/ui/input";
import { formatPhone } from "@/lib/phone";
import { EmptyBranchNote } from "@/components/EmptyBranchNote";

const FILTERS = [
  { key: "", label: "All" },
  { key: "tax_invoice", label: "Tax Invoices" },
  { key: "gold_bullion", label: "Bullion" },
  { key: "loan", label: "Loans" },
  { key: "buying_contract", label: "Buying" },
];

export default function DocumentsList() {
  const { branch } = useAuth();
  const [docType, setDocType] = useState("");
  const [search, setSearch] = useState("");
  const [allBranches, setAllBranches] = useState(false);
  const [hideVoid, setHideVoid] = useState(false);
  const [rows, setRows] = useState([]);

  useEffect(() => {
    const t = setTimeout(() => {
      api
        .get("/documents", {
          params: {
            doc_type: docType || undefined,
            branch: allBranches ? undefined : branch,
            search: search || undefined,
            include_void: !hideVoid,
          },
        })
        .then(({ data }) => setRows(data))
        .catch(() => setRows([]));
    }, 250);
    return () => clearTimeout(t);
  }, [docType, search, branch, allBranches, hideVoid]);

  return (
    <div className="fade-up space-y-6" data-testid="documents-list-page">
      <div>
        <h1 className="text-3xl font-bold sm:text-4xl">All Records</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Every invoice, loan and contract, archived and reprintable.
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="flex flex-wrap gap-px border border-border bg-border">
          {FILTERS.map((f) => (
            <button
              key={f.key || "all"}
              onClick={() => setDocType(f.key)}
              data-testid={`filter-${f.key || "all"}`}
              className={`px-4 py-2 text-sm transition-colors duration-150 ${
                docType === f.key
                  ? "bg-[#0A0A0A] font-semibold text-white"
                  : "bg-white hover:bg-[#FBF8F2]"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
        <div className="relative min-w-[220px] flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            className="h-10 rounded-none pl-9"
            placeholder="Search name, phone or number"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="documents-search-input"
          />
        </div>
        <label className="flex cursor-pointer items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={allBranches}
            onChange={(e) => setAllBranches(e.target.checked)}
            data-testid="all-branches-checkbox"
            className="accent-[#C5A059]"
          />
          Both branches
        </label>
        <label className="flex cursor-pointer items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={hideVoid}
            onChange={(e) => setHideVoid(e.target.checked)}
            data-testid="hide-void-checkbox"
            className="accent-[#C5A059]"
          />
          Hide voided
        </label>
      </div>

      <div className="overflow-x-auto border border-border">
        <table className="w-full min-w-[820px] text-sm" data-testid="documents-table">
          <thead className="bg-[#F8F9FA]">
            <tr className="border-b border-border">
              <th className="px-3 py-2.5 text-left font-semibold">No.</th>
              <th className="px-3 py-2.5 text-left font-semibold">Type</th>
              <th className="px-3 py-2.5 text-left font-semibold">Customer</th>
              <th className="px-3 py-2.5 text-left font-semibold">Phone</th>
              <th className="px-3 py-2.5 text-left font-semibold">Branch</th>
              <th className="px-3 py-2.5 text-left font-semibold">Date</th>
              <th className="px-3 py-2.5 text-right font-semibold">Total</th>
              <th className="px-3 py-2.5 text-right font-semibold">Open</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {rows.length === 0 && (
              <tr>
                <td colSpan={8} className="px-3 py-10 text-center text-muted-foreground">
                  No records found.
                  {!search && <EmptyBranchNote className="mx-auto mt-4 max-w-xl" />}
                </td>
              </tr>
            )}
            {rows.map((d) => (
              <tr
                key={d.id}
                className="transition-colors duration-150 hover:bg-[#FBF8F2]"
                data-testid={`document-row-${d.doc_number}`}
              >
                <td className="tabular px-3 py-2.5 font-semibold">
                  #{d.doc_number}
                  {d.status === "void" && (
                    <span
                      className="ml-2 bg-[#9A3412] px-1.5 py-0.5 text-[10px] font-bold uppercase text-white"
                      data-testid={`void-badge-${d.doc_number}`}
                    >
                      Void
                    </span>
                  )}
                </td>
                <td className="px-3 py-2.5">{DOC_LABELS[d.doc_type]}</td>
                <td className="px-3 py-2.5">{d.customer_name}</td>
                <td className="tabular whitespace-nowrap px-3 py-2.5">{d.customer_phone ? formatPhone(d.customer_phone) : "-"}</td>
                <td className="px-3 py-2.5 text-xs uppercase tracking-wider">{d.branch}</td>
                <td className="tabular whitespace-nowrap px-3 py-2.5">{d.doc_date}</td>
                <td className="tabular px-3 py-2.5 text-right font-semibold">
                  {money(d.total)}
                  {d.status !== "void" && d.doc_type === "loan" && (
                    <span
                      className={`ml-2 px-1.5 py-0.5 text-[10px] font-bold uppercase ${
                        (d.loan_due || 0) <= 0.005
                          ? "bg-[#166534] text-white"
                          : "bg-[#9A3412] text-white"
                      }`}
                      data-testid={`payment-badge-${d.doc_number}`}
                    >
                      {(d.loan_due || 0) <= 0.005 ? "Repaid" : `Due ${money(d.loan_due)}`}
                    </span>
                  )}
                  {d.status !== "void" &&
                    d.doc_type !== "buying_contract" &&
                    d.doc_type !== "loan" && (
                      <span
                        className={`ml-2 px-1.5 py-0.5 text-[10px] font-bold uppercase ${
                          (d.balance || 0) <= 0.005 && (d.total_paid || 0) > 0
                            ? "bg-[#166534] text-white"
                            : "bg-[#9A3412] text-white"
                        }`}
                        data-testid={`payment-badge-${d.doc_number}`}
                      >
                        {(d.balance || 0) < -0.005
                          ? `Credit ${money(-d.balance)}`
                          : Math.abs(d.balance || 0) <= 0.005 && (d.total_paid || 0) > 0
                            ? "Paid"
                            : `Due ${money(d.balance)}`}
                      </span>
                    )}
                </td>
                <td className="px-3 py-2.5 text-right">
                  <Link
                    to={`/documents/${d.id}`}
                    className="text-[#B38F4E] hover:underline"
                    data-testid={`open-document-${d.doc_number}`}
                  >
                    View
                  </Link>
                  <Link
                    to={`/new/${d.doc_type}?from=${d.id}`}
                    className="ml-3 text-[#B38F4E] hover:underline"
                    data-testid={`reorder-document-${d.doc_number}`}
                  >
                    Reorder
                  </Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
